import i18n from '@/locales/i18n';
/**
 * Agent type constants for the platform
 * - SELF_PLANNED (1): Conversational agent with flow editor
 * - AI_WRITING (2): AI writing/content generation agent
 * - MULTI_AGENT (3): Multi-agent orchestration
 * - VOICE (4): End-to-end voice agent
 */
export const AGENT_TYPE = {
  SELF_PLANNED: 1,
  AI_WRITING: 2,
  MULTI_AGENT: 3,
  VOICE: 4
};

/**
 * Default long memory configuration (nested under basic_config.long_memory)
 */
export const DEFAULT_LONG_MEMORY = {
  long_term_memory_scope: 1,
  long_term_memory_count: 3,
  long_term_memory_synchronous: 0,
  long_term_memory_robots: [],
  is_user_profile_custom: 0,
  user_profile_custom_content: [
    {
      info: [
        i18n.global.t('constants.agentTypes.80be00114cfc641ed9f3d40619364437'),
        i18n.global.t('constants.agentTypes.39fe10ac9f2e595524dd6a8bba14ec78'),
        i18n.global.t('constants.agentTypes.8549ca78198cd3b986c0ba1c16cdaca0'),
        i18n.global.t('constants.agentTypes.1607c23074aa6a05f89be30b5f828230'),
        i18n.global.t('constants.agentTypes.605e1e6ce750e219327639a9bbc4b9a4'),
        i18n.global.t('constants.agentTypes.39852c6f31198b1456edd95b36985e0f'),
        i18n.global.t('constants.agentTypes.efeb2e3b50ade0cafd50b36e2154f3f5')
      ],
      category: i18n.global.t('constants.agentTypes.ea23439fe3522ed80b2276aed305841a')
    },
    {
      info: [
        i18n.global.t('constants.agentTypes.acd16e5b4f1e6ffc70b78a79fb579648'),
        i18n.global.t('constants.agentTypes.eb43b7341ef842b9223bd15c5bcef155')
      ],
      category: i18n.global.t('constants.agentTypes.165c8a0156e44e095a4e4281b121e2b6')
    },
    {
      info: [
        i18n.global.t('constants.agentTypes.33bd6fd02e1f4b02c132bfd7c0d9f05e'),
        i18n.global.t('constants.agentTypes.25c079cc079b328909e1b97207b6af94'),
        i18n.global.t('constants.agentTypes.4f2e84f579e254b10528c99eb250abca')
      ],
      category: i18n.global.t('constants.agentTypes.76286007307304250d1a09b1a18d1d9c')
    },
    {
      info: [
        i18n.global.t('constants.agentTypes.bbaaf3b8f95dc80b49574d27c0370cfd'),
        i18n.global.t('constants.agentTypes.ce84133f8ad8bb4597d06a345d7e9223'),
        i18n.global.t('constants.agentTypes.ed33ae15636b685a2bb9ecde620ea1ee')
      ],
      category: i18n.global.t('constants.agentTypes.b68aa7a067a4fc282712aec6d4a6af44')
    }
  ],
  is_user_memory_points_custom: 0,
  user_memory_points_prompt: '',
  user_memory_points_collect_type: 0,
  delay_minute: 5,
  is_user_memory_variable: 0,
  user_memory_variable_content: []
};

/**
 * Default voice agent configuration (matching API structure)
 */
export const DEFAULT_VOICE_CONFIG = {
  // Basic configuration
  basic_config: {
    welcome_message: '',
    prompt: '',
    voice_llm: {},
    knowledge_retrieval_enable: false,
    knowledge_list: [],
    is_tool_enable: true,
    tool_list: [],
    tip_message_variable: [],
    variable_config: {
      system_variables: [],
      custom_variables: []
    },
    is_short_term_memory_enable: false,
    /** API may still return this at basic_config root; UI uses short_memory.max_context_num */
    max_context_num: 10,
    short_memory: {
      max_context_num: 10
    },
    is_long_term_memory_enable: false,
    long_memory: {
      ...DEFAULT_LONG_MEMORY
    }
  },
  // Realtime configuration
  realtime_config: {
    id: null,
    robot_id: null,
    is_delete: false,
    timbre_id: null,
    created_by: '',
    tts_config: {},
    create_time: '',
    modified_by: '',
    tts_channel: {},
    update_time: '',
    model_id_tts: null,
    model_id_asr: null,
    asr_model_name: '',
    asr_config: {},
    max_call_time: null,
    tts_model_name: '',
    tts_display_name: '',
    turn_detection: 0,
    allow_interrupt: 1,
    emotion_enable: false,
    voiceprint_enabled: 0,
    silence_prompt_enabled: 0,
    is_terminal_sentence_enable: false,
    created_nickname: '',
    modified_nickname: '',
    initial_interact_mode: 0,
    turn_detection_config: {
      wait_time: 5,
      semantics_filter: 0,
      turn_tip: ''
    },
    voiceprint_config: '',
    interrupt_config: {
      type: 1,
      interrupt_check_type: 0,
      interrupt_timing: 0,
      head_second: 3,
      warn_on_continuous: 1,
      warn_on_total: 1,
      warn_on_exceed: 1,
      continuous_n: 2,
      total_n: 5,
      exceed_count: 2,
      interrupt_tip: '',
      warn_texts: null
    },
    silence_prompt_config: {
      max_time: 3000,
      prompt: '',
      silence_action_tabs: [
        {
          turn_num: 3,
          max_time: 3000,
          prompt: '',
          action_type: 1
        },
        {
          turn_num: 5,
          max_time: 5000,
          prompt: '',
          action_type: 2
        }
      ]
    },
    terminal_sentence_config: {
      user_sentence: [],
      terminal_prompt: '',
      system_sentence: []
    },
    vad: {
      type: 'silero',
      dynamic_params_enable: false,
      silero: {
        enable: 1,
        description: '',
        phonecall: {},
        webcall: {},
        webcall_llm_speaking: {},
        webcall_user_speaking: {}
      },
      ten: {
        enable: 1,
        description: '',
        phonecall: {},
        webcall: {},
        webcall_llm_speaking: {},
        webcall_user_speaking: {}
      }
    },
    side_info_type: 0
  }
};

/**
 * Available voices for OpenAI Realtime (kept for backward compatibility)
 */
export const OPENAI_REALTIME_VOICES = [
  {
    value: 'alloy',
    label: 'Alloy'
  },
  {
    value: 'echo',
    label: 'Echo'
  },
  {
    value: 'fable',
    label: 'Fable'
  },
  {
    value: 'onyx',
    label: 'Onyx'
  },
  {
    value: 'nova',
    label: 'Nova'
  },
  {
    value: 'shimmer',
    label: 'Shimmer'
  }
];

/**
 * Available models for OpenAI Realtime (kept for backward compatibility)
 */
export const OPENAI_REALTIME_MODELS = [
  {
    value: 'gpt-4o-realtime-preview',
    label: 'GPT-4o Realtime Preview'
  },
  {
    value: 'gpt-4o-mini-realtime-preview',
    label: 'GPT-4o Mini Realtime Preview'
  }
];
